'use client';

import type { ICollectionPipelineStatusV3 } from '@repo/sdk';
import { clipSubtitleV1Api } from '@repo/sdk';
import type { JSX } from 'react';
import { useCallback } from 'react';
import { PipelineStepCell } from '../pipeline-step-cell';
import { PipelineStepCountBadge } from '../pipeline-step-count-badge';
import { PipelineStepDialog } from '../pipeline-step-dialog';
import { useStepAction } from '../use-step-action';
import { formatRanAt } from './format-ran-at';
import { StepLogPanel } from './step-log-panel';

interface IClipStepProps {
  item: ICollectionPipelineStatusV3;
}

function resolveDisabled(item: ICollectionPipelineStatusV3): string | undefined {
  if (item.subtitleId === null) {
    return 'SRT not imported yet';
  }
  if (item.steps.matched.ranAt === null) {
    return 'Match not run yet';
  }
  return undefined;
}

export function ClipStep({ item }: IClipStepProps): JSX.Element {
  const { clipped } = item.steps;
  const subtitleId = item.subtitleId;
  const start = useCallback(() => {
    if (subtitleId === null) {
      throw new Error('SRT not imported yet');
    }
    return clipSubtitleV1Api({ subtitleId, force: clipped.ranAt !== null });
  }, [subtitleId, clipped.ranAt]);
  const action = useStepAction({ start });
  const disabled = resolveDisabled(item);

  return (
    <PipelineStepCell
      badge={<PipelineStepCountBadge step={clipped} />}
      renderDialog={(open, onOpenChange) => (
        <PipelineStepDialog
          open={open}
          onOpenChange={onOpenChange}
          title="Matched Clips"
          description={`Selects at most one clip per subtitle entry from match candidates. Deterministic (no AI). Re-run is safe and cheap. ${formatRanAt(clipped.ranAt)} · ${clipped.count} clips.`}
          dataHref={`/sys/media?collectionId=${item.collectionId}`}
          dataLabel="Open clips"
          primaryAction={{
            label: clipped.ranAt === null ? 'Generate matched clips' : 'Re-run (force)',
            onClick: action.run,
            pending: action.status === 'running',
            disabledReason: disabled,
          }}
          body={<StepLogPanel status={action.status} log={action.log} />}
        />
      )}
    />
  );
}
